/**
 * Pure helpers of the log viewer (CO-150): splitting raw output into lines,
 * telling errors and warnings apart, folding repeated progress lines, and
 * picking the line worth quoting from a long error. Terminal output is not
 * source code, so nothing here pretends to parse it: patterns are cautious
 * and a line they do not recognise is plain text.
 */

export interface ViewerLine {
  key: string;
  /** "12:03:04" */
  at?: string;
  /** info, warning, error, debug — from the record, or guessed from the text. */
  level?: string;
  /** Where it came from (agent, engine), when recorded. */
  source?: string;
  text: string;
}

const errorPattern = /\b(ERR_[A-Z0-9_]+|ERR!|[A-Z_]+_FAILED|FATAL|Fatal error|Uncaught|Unauthorized|exit status [1-9]\d*)\b|^\s*(error|Error)\b|\bError:/;
const warningPattern = /^\s*(WARN|warn|warning|Warning)\b|\bWARN\b|\bdeprecated\b/;

/** A level for a line that has none: error or warning only on a clear sign. */
export function guessLevel(text: string): string | undefined {
  if (errorPattern.test(text)) return 'error';
  if (warningPattern.test(text)) return 'warning';
  return undefined;
}

/** Raw multi-line output as viewer lines. */
export function linesOf(raw: string, keyPrefix = 'line'): ViewerLine[] {
  return raw
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .map((text, index) => ({ key: `${keyPrefix}-${index}`, text, level: guessLevel(text) }))
    .filter((line, index, all) => line.text.trim() !== '' || (index > 0 && index < all.length - 1));
}

/** The engine writes "warn" and "warning"; both are one level. */
export function normalizeLevel(level: string | undefined): string | undefined {
  if (!level) return undefined;
  const value = level.toLowerCase();
  return value === 'warn' ? 'warning' : value;
}

/** Lines that differ only in their numbers ("Progress: resolved 739, downloaded 43"). */
function shapeOf(text: string): string {
  return text.replace(/\d+/g, '#').trim();
}

export type Row =
  | { kind: 'line'; line: ViewerLine; number: number }
  | { kind: 'fold'; key: string; count: number; lines: { line: ViewerLine; number: number }[] };

/**
 * Folds runs of at least `min` consecutive lines of the same shape into one
 * row, keeping the first and the last visible: 30 "Progress: …" lines say
 * one thing.
 */
export function foldRepeats(lines: ViewerLine[], min = 5): Row[] {
  const rows: Row[] = [];
  let i = 0;
  while (i < lines.length) {
    let j = i + 1;
    const shape = shapeOf(lines[i]!.text);
    while (j < lines.length && shape !== '' && shapeOf(lines[j]!.text) === shape) j += 1;
    const run = j - i;
    if (run >= min) {
      rows.push({ kind: 'line', line: lines[i]!, number: i + 1 });
      rows.push({
        kind: 'fold',
        key: `fold-${lines[i]!.key}`,
        count: run - 2,
        lines: lines.slice(i + 1, j - 1).map((line, k) => ({ line, number: i + 2 + k })),
      });
      rows.push({ kind: 'line', line: lines[j - 1]!, number: j });
    } else {
      for (let k = i; k < j; k += 1) rows.push({ kind: 'line', line: lines[k]!, number: k + 1 });
    }
    i = j;
  }
  return rows;
}

/** Indexes of the lines that contain the query (case-insensitive). */
export function matchingLines(lines: ViewerLine[], query: string): number[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [];
  const out: number[] = [];
  lines.forEach((line, index) => {
    if (`${line.source ?? ''} ${line.text}`.toLowerCase().includes(needle)) out.push(index);
  });
  return out;
}

/** Splits text around the query, for highlighting. */
export function highlightParts(text: string, query: string): { text: string; match: boolean }[] {
  const needle = query.trim();
  if (!needle) return [{ text, match: false }];
  const lower = text.toLowerCase();
  const target = needle.toLowerCase();
  const parts: { text: string; match: boolean }[] = [];
  let from = 0;
  for (;;) {
    const at = lower.indexOf(target, from);
    if (at < 0) break;
    if (at > from) parts.push({ text: text.slice(from, at), match: false });
    parts.push({ text: text.slice(at, at + needle.length), match: true });
    from = at + needle.length;
  }
  if (from < text.length) parts.push({ text: text.slice(from), match: false });
  return parts;
}

const codePattern = /\b(ERR_[A-Z0-9_]+|[A-Z][A-Z0-9_]*_FAILED|E[0-9]{3})\b/;

/**
 * The line worth quoting from a long error: the package manager's or the
 * tool's own error line before the wrapper's ("FRONTEND_INSTALL_FAILED:
 * docker run …"), shortened.
 */
export function excerptOf(raw: string, max = 220): string {
  const lines = raw.replace(/\r\n?/g, '\n').split('\n').map((line) => line.trim()).filter(Boolean);
  const specific = lines.find((line) => /\bERR_[A-Z0-9_]+\b|npm (ERR!|error)|\bError:|VERIFICATION_FAILED|Uncaught|Fatal error/.test(line) && !/^[A-Z_]+_FAILED: docker run/.test(line));
  const chosen = specific ?? lines.find((line) => guessLevel(line) === 'error') ?? lines[0] ?? '';
  return chosen.length > max ? `${chosen.slice(0, max - 1)}…` : chosen;
}

/** An error code to search the log for ("ERR_PNPM_FETCH_401"). */
export function errorCodeOf(raw: string): string | null {
  const preferred = raw.match(/\bERR_[A-Z0-9_]+\b/);
  if (preferred) return preferred[0];
  return raw.match(codePattern)?.[0] ?? null;
}
